/**
 * RoomModeToggle is an owner-only switch for a persistent room mode, such as
 * managed mode or curation. Shows the room's refusal beneath the switch.
 * A mode with its own settings puts a button for them beside the switch and
 * the settings themselves beneath it.
 */
import * as React from 'react'
import { Switch } from '@merels/ui'

export interface RoomModeToggleProps {
  label: string
  description: string
  checked: boolean
  /** Rejects with the room's reason when the change is refused. */
  onToggle: (next: boolean) => Promise<void>
  testId: string
  /** A control beside the switch, such as a button that opens the mode's settings. */
  accessory?: React.ReactNode
  children?: React.ReactNode
}

export function RoomModeToggle({ label, description, checked, onToggle, testId, accessory, children }: RoomModeToggleProps) {
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleChange = async (next: boolean) => {
    if (isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      await onToggle(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to change ${label.toLowerCase()}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={testId} className="text-sm font-semibold text-primary">
          {label}
        </label>
        <div className="flex items-center gap-2">
          {accessory}
          <Switch id={testId} data-testid={testId} checked={checked} disabled={isSaving} onCheckedChange={handleChange} />
        </div>
      </div>
      <p className="text-xs text-muted">{description}</p>
      {error && <p className="text-xs text-danger" role="alert">{error}</p>}
      {children}
    </div>
  )
}
